import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  workflowApi,
  WorkflowCreateRequest,
} from "@/services/workflowApi";
import { WorkflowUpdateRequest } from "@/types/workflow";
import { toast } from "@/components/ui/use-toast";

export const useWorkflows = () => {
  return useQuery({
    queryKey: ["workflows"],
    queryFn: () => workflowApi.getAll(),
  });
};

export const useWorkflow = (id: string) => {
  return useQuery({
    queryKey: ["workflows", id],
    queryFn: () => workflowApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateWorkflow = (options?: {
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: WorkflowCreateRequest) => workflowApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
      toast({
        title: "Success",
        description: "Workflow created successfully",
      });
      // Call additional success callback if provided
      options?.onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.response?.data?.detail || "Failed to create workflow",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateWorkflow = (options?: {
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: WorkflowUpdateRequest }) => 
      workflowApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
      queryClient.invalidateQueries({ queryKey: ["workflows", id] });
      toast({
        title: "成功",
        description: "工作流程更新成功",
      });
      options?.onSuccess?.();
    },
    onError: (error: any) => {
      const errorDetail = error.response?.data?.detail;
      let description = "更新工作流程失敗";
      
      if (errorDetail) {
        if (typeof errorDetail === 'string') {
          description = errorDetail;
        } else if (Array.isArray(errorDetail)) {
          description = errorDetail.map(err => err.msg || err).join(', ');
        } else if (typeof errorDetail === 'object') {
          description = JSON.stringify(errorDetail);
        }
      }
      
      toast({
        title: "錯誤",
        description,
        variant: "destructive",
      });
    },
  });
};

export const useDeleteWorkflow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => workflowApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
      toast({
        title: "Success",
        description: "Workflow deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.response?.data?.detail || "Failed to delete workflow",
        variant: "destructive",
      });
    },
  });
};